import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  KeyRound,
  Link2,
  Sparkles,
  Trophy,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { auth, db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  documentId,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { DashboardHeader, DashboardSidebar } from "./Dashboard";
import { fetchStudentResults } from "./utils/studentAssessmentApi";
import { formatJoinCodeInput, normalizeJoinCode as normalizeCode } from "./utils/joinCodes";
import "./joinTeacher.css";

const FIRESTORE_IN_LIMIT = 10;

const chunkArray = (items, size = FIRESTORE_IN_LIMIT) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const formatTeacherName = (profile = {}) => {
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
  return (
    profile.displayName ||
    fullName ||
    profile.username ||
    profile.teacherName ||
    profile.email ||
    "Teacher"
  );
};

const toDate = (value) => {
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  if (value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "Pending" || value === "N/A") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getQuizKey = (submission) => String(submission.quizId || submission.quizCode || "unknown").trim();

const JoinTeacher = ({ user }) => {
  const navigate = useNavigate();
  const [teacherCode, setTeacherCode] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isHubLoading, setIsHubLoading] = useState(false);
  const [hubError, setHubError] = useState("");
  const [teacherCards, setTeacherCards] = useState([]);
  const [expandedTeacherIds, setExpandedTeacherIds] = useState({});
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    const syncTheme = () => {
      const nextMode = localStorage.getItem("darkMode") === "true";
      setDarkMode(nextMode);
      document.body.classList.toggle("dark-mode", nextMode);
      document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
    };
    syncTheme();
    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  const loadTeacherHub = useCallback(async () => {
    if (!user?.uid) return;

    setIsHubLoading(true);
    setHubError("");

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      const rawTeacherCodes = Array.isArray(userData.teacherCodes)
        ? userData.teacherCodes
        : userData.teacherCode
          ? [userData.teacherCode]
          : [];
      const teacherCodes = Array.from(
        new Map(
          rawTeacherCodes
            .map((code) => String(code || "").trim())
            .filter(Boolean)
            .map((code) => [normalizeCode(code), code])
        ).values()
      );

      if (teacherCodes.length === 0) {
        setTeacherCards([]);
        return;
      }

      const teacherProfilesByCode = new Map();
      const teacherProfilesByUid = new Map();
      await Promise.all(
        chunkArray(teacherCodes).map(async (codeChunk) => {
          const teachersQuery = query(
            collection(db, "users"),
            where("teacherCode", "in", codeChunk)
          );
          const snapshot = await getDocs(teachersQuery);
          snapshot.docs.forEach((teacherDoc) => {
            const profile = { uid: teacherDoc.id, id: teacherDoc.id, ...teacherDoc.data() };
            const codeKey = normalizeCode(profile.teacherCode);
            if (codeKey) teacherProfilesByCode.set(codeKey, profile);
            teacherProfilesByUid.set(teacherDoc.id, profile);
          });
        })
      );

      const membershipsQuery = query(
        collection(db, "classMemberships"),
        where("studentUid", "==", user.uid),
        where("active", "==", true)
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);
      const memberships = membershipsSnapshot.docs.map((membershipDoc) => ({
        id: membershipDoc.id,
        ...membershipDoc.data(),
      }));
      const membershipsByTeacherCode = new Map();
      const membershipsByTeacherUid = new Map();
      memberships.forEach((membership) => {
        const codeKey = normalizeCode(membership.teacherCode);
        if (codeKey) {
          if (!membershipsByTeacherCode.has(codeKey)) membershipsByTeacherCode.set(codeKey, []);
          membershipsByTeacherCode.get(codeKey).push(membership);
        }
        if (membership.teacherUid) {
          if (!membershipsByTeacherUid.has(membership.teacherUid)) membershipsByTeacherUid.set(membership.teacherUid, []);
          membershipsByTeacherUid.get(membership.teacherUid).push(membership);
        }
      });

      const submissions = await fetchStudentResults();

      const quizIds = Array.from(new Set(submissions.map((sub) => String(sub.quizId || "").trim()).filter(Boolean)));
      const quizCodes = Array.from(new Set(submissions.map((sub) => String(sub.quizCode || "").trim()).filter(Boolean)));
      const quizzesById = new Map();
      const quizzesByCode = new Map();

      await Promise.all([
        ...chunkArray(quizIds).map(async (idChunk) => {
          const quizzesQuery = query(collection(db, "quizzes"), where(documentId(), "in", idChunk));
          const snapshot = await getDocs(quizzesQuery);
          snapshot.docs.forEach((quizDoc) => {
            const quiz = { id: quizDoc.id, ...quizDoc.data() };
            quizzesById.set(quizDoc.id, quiz);
            if (quiz.quizCode) quizzesByCode.set(String(quiz.quizCode), quiz);
          });
        }),
        ...chunkArray(quizCodes).map(async (codeChunk) => {
          const quizzesQuery = query(collection(db, "quizzes"), where("quizCode", "in", codeChunk));
          const snapshot = await getDocs(quizzesQuery);
          snapshot.docs.forEach((quizDoc) => {
            const quiz = { id: quizDoc.id, ...quizDoc.data() };
            quizzesById.set(quizDoc.id, quiz);
            if (quiz.quizCode) quizzesByCode.set(String(quiz.quizCode), quiz);
          });
        }),
      ]);

      const activityByTeacherUid = new Map();
      const groupedSubmissions = new Map();
      submissions.forEach((submission) => {
        const quiz = quizzesById.get(String(submission.quizId || "")) || quizzesByCode.get(String(submission.quizCode || ""));
        const teacherUid = quiz?.creatorUid;
        if (!teacherUid) return;

        const quizKey = getQuizKey(submission);
        const groupKey = `${teacherUid}:${quizKey}`;
        if (!groupedSubmissions.has(groupKey)) {
          groupedSubmissions.set(groupKey, {
            teacherUid,
            quiz,
            quizKey,
            submissions: [],
          });
        }
        groupedSubmissions.get(groupKey).submissions.push(submission);
      });

      groupedSubmissions.forEach((group) => {
        const completedSubmissions = group.submissions.filter((submission) => numericValue(submission.markAwarded) !== null);
        if (completedSubmissions.length === 0) return;

        let totalAwarded = 0;
        let totalMax = 0;
        completedSubmissions.forEach((submission) => {
          const awarded = numericValue(submission.markAwarded);
          const maxMark = numericValue(submission.maxMark);
          if (awarded !== null) totalAwarded += awarded;
          if (maxMark !== null) totalMax += maxMark;
        });

        const submittedDates = completedSubmissions.map((submission) => toDate(submission.submittedAt)).filter(Boolean);
        const latestDate = submittedDates.length
          ? submittedDates.reduce((latest, date) => (date > latest ? date : latest), submittedDates[0])
          : null;
        const activity = {
          id: group.quiz?.id || group.quizKey,
          quizId: group.quiz?.id || "",
          quizCode: group.quiz?.quizCode || group.quizKey,
          title: group.quiz?.quizTitle || group.quiz?.title || group.quiz?.quizCode || group.quizKey || "Assessment",
          score: Number.isFinite(totalAwarded) ? totalAwarded : null,
          maxScore: totalMax > 0 ? totalMax : null,
          submittedAt: latestDate,
          percentage: totalMax > 0 ? Math.round((totalAwarded / totalMax) * 100) : null,
        };

        if (!activityByTeacherUid.has(group.teacherUid)) activityByTeacherUid.set(group.teacherUid, []);
        activityByTeacherUid.get(group.teacherUid).push(activity);
      });

      const cards = teacherCodes.map((code) => {
        const codeKey = normalizeCode(code);
        const codeMemberships = membershipsByTeacherCode.get(codeKey) || [];
        const membershipTeacherUid = codeMemberships.find((membership) => membership.teacherUid)?.teacherUid || "";
        const codeProfile = teacherProfilesByCode.get(codeKey) || {};
        const uidProfile = membershipTeacherUid ? teacherProfilesByUid.get(membershipTeacherUid) || {} : {};
        const profile = Object.keys(codeProfile).length > 0 ? codeProfile : uidProfile;
        const teacherUid = profile.uid || profile.id || membershipTeacherUid;
        const relatedMemberships = [
          ...codeMemberships,
          ...(teacherUid ? membershipsByTeacherUid.get(teacherUid) || [] : []),
        ];
        const classesById = new Map();
        relatedMemberships.forEach((membership) => {
          const classKey = membership.classId || membership.classCode || membership.className;
          if (classKey) classesById.set(classKey, membership);
        });
        const classes = Array.from(classesById.values())
          .map((membership) => ({
            id: membership.classId || membership.classCode || membership.className,
            className: membership.className || membership.classCode || "Class",
            classCode: membership.classCode || "",
          }))
          .filter((classItem) => classItem.className);
        const activity = (teacherUid ? activityByTeacherUid.get(teacherUid) || [] : [])
          .sort((a, b) => (b.submittedAt?.getTime?.() || 0) - (a.submittedAt?.getTime?.() || 0));
        const percentages = activity.map((item) => item.percentage).filter((value) => Number.isFinite(value));
        const averagePercentage = percentages.length
          ? Math.round(percentages.reduce((sum, value) => sum + value, 0) / percentages.length)
          : null;

        return {
          id: teacherUid || codeKey,
          teacherUid,
          name: formatTeacherName(profile),
          teacherCode: profile.teacherCode || code,
          classes,
          activity,
          completedCount: activity.length,
          averagePercentage,
        };
      });

      setTeacherCards(cards);
    } catch (error) {
      console.error("Error loading teacher connections:", error);
      setHubError("Could not load your teachers. Please refresh and try again.");
    } finally {
      setIsHubLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTeacherHub();
  }, [loadTeacherHub]);

  const handleTeacherCodeChange = (e) => {
    setTeacherCode(formatJoinCodeInput(e.target.value));
    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Issued teacher codes are uppercase; Firestore equality is case-sensitive.
    const enteredCode = normalizeCode(teacherCode);
    if (!enteredCode) {
      setMessage({
        text: "Please enter a teacher code.",
        type: "error",
      });
      return;
    }

    setIsJoining(true);
    setMessage({ text: "", type: "" });

    try {
      const tenantsRef = collection(db, "tenants");
      const tenantsQuery = query(tenantsRef, where("teacherCode", "==", enteredCode));
      const querySnapshot = await getDocs(tenantsQuery);

      if (querySnapshot.empty) {
        setMessage({
          text: "Teacher code is invalid.",
          type: "error",
        });
        setIsJoining(false);
        return;
      }

      const teacherDoc = querySnapshot.docs[0];
      const teacherData = teacherDoc.data();
      const teacherName = teacherData.teacherName || teacherData.displayName || "this teacher";
      const canonicalTeacherCode = teacherData.teacherCode || enteredCode;

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      const existingTeacherCodes = Array.isArray(userData.teacherCodes) ? userData.teacherCodes : [];
      const alreadyLinked = existingTeacherCodes.some(
        (code) => normalizeCode(code) === normalizeCode(canonicalTeacherCode) || normalizeCode(code) === normalizeCode(enteredCode)
      );

      if (alreadyLinked) {
        setMessage({
          text: "You're already connected to this teacher.",
          type: "info",
        });
        setIsJoining(false);
        return;
      }

      await updateDoc(userDocRef, {
        tenant: teacherDoc.id,
        teacherCodes: arrayUnion(canonicalTeacherCode),
        assignedBy: teacherName,
      });

      setTeacherCode("");
      setMessage({
        text: `Successfully linked to ${teacherName}.`,
        type: "success",
      });
      await loadTeacherHub();
      setIsJoining(false);
    } catch (error) {
      console.error("Error joining teacher:", error);
      setMessage({
        text: "An error occurred. Please try again.",
        type: "error",
      });
      setIsJoining(false);
    }
  };

  const summary = useMemo(() => {
    const teacherCount = teacherCards.length;
    const completedCount = teacherCards.reduce((sum, teacher) => sum + teacher.completedCount, 0);
    const classCount = teacherCards.reduce((sum, teacher) => sum + teacher.classes.length, 0);
    return { teacherCount, completedCount, classCount };
  }, [teacherCards]);

  const visibleTeacherCards = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return teacherCards;
    return teacherCards.filter((teacher) => [
      teacher.name,
      teacher.teacherCode,
      ...teacher.classes.map((classItem) => classItem.className),
    ].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [search, teacherCards]);

  const toggleTeacherActivity = (teacherId) => {
    setExpandedTeacherIds((prev) => ({
      ...prev,
      [teacherId]: !prev[teacherId],
    }));
  };

  const openAssessmentResult = (activity) => {
    const params = new URLSearchParams();
    if (activity.quizId) params.set("quizId", activity.quizId);
    if (activity.quizCode) params.set("quizCode", activity.quizCode);
    if (!activity.quizId && !activity.quizCode) params.set("quizCode", activity.id);
    const query = params.toString();
    navigate(`/view-my-scores?${query}`);
  };

  const toggleTheme = () => {
    const nextMode = !darkMode;
    localStorage.setItem("darkMode", String(nextMode));
    setDarkMode(nextMode);
    document.body.classList.toggle("dark-mode", nextMode);
    document.documentElement.setAttribute("data-dark-mode", nextMode ? "true" : "false");
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const username = user?.displayName || user?.email?.split("@")[0] || "Student";

  return (
    <div className={`sd-dashboard sd-brand-theme jt-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar role="student" username={username} onLogout={handleLogout} officialDashboardBrand
        open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePath="/join-teacher" />
      <div className="sd-workspace jt-workspace">
        <DashboardHeader username={username} role="student" darkMode={darkMode}
          onToggleTheme={toggleTheme} onOpenMenu={() => setSidebarOpen(true)}
          search={search} onSearch={setSearch} />

        <main className="sd-main jt-main">
          <section className="jt-hero">
            <div className="jt-hero-copy">
              <p className="jt-kicker">Connect with your teacher</p>
              <h1>Join Teacher</h1>
              <p>Enter your teacher code to connect your account and access your classes and assessments.</p>
            </div>
            <div className="jt-hero-art" aria-hidden="true">
              <span><UserPlus size={32} /></span>
              <i className="jt-orbit jt-orbit-one"><Sparkles size={17} /></i>
              <i className="jt-orbit jt-orbit-two"><BookOpen size={18} /></i>
              <i className="jt-orbit jt-orbit-three"><Link2 size={17} /></i>
            </div>
          </section>

          <section className="jt-metrics" aria-label="Teacher connection summary">
            <article className="jt-metric jt-metric-teachers"><span><UsersRound size={22} /></span><div><strong>{summary.teacherCount}</strong><h2>Teachers Connected</h2><p>Your teacher network</p></div></article>
            <article className="jt-metric jt-metric-classes"><span><GraduationCap size={22} /></span><div><strong>{summary.classCount}</strong><h2>Classes Joined</h2><p>Across connected teachers</p></div></article>
            <article className="jt-metric jt-metric-results"><span><Trophy size={22} /></span><div><strong>{summary.completedCount}</strong><h2>Completed Results</h2><p>Marked assessment activity</p></div></article>
          </section>

        <div className="jt-content">
          <div className="jt-connect-grid">
          <div className="jt-card">
            <div className="jt-card-heading"><span><KeyRound size={21} /></span><div><h2 className="jt-card-title">Join a Teacher</h2><p>Connect your StudyDen account</p></div></div>
            <p className="jt-card-instruction">Ask your teacher for their unique code, then enter it below to connect.</p>
            <form onSubmit={handleSubmit} className="jt-form">
              <div className="jt-field">
                <label className="jt-label" htmlFor="teacherCode">Teacher Code</label>
                <div className="jt-input-shell"><KeyRound size={19} />
                  <input id="teacherCode" type="text" className="jt-input"
                    value={teacherCode} onChange={handleTeacherCodeChange}
                    placeholder="e.g. AI-TEACHER-123" disabled={isJoining} autoComplete="off" />
                </div>
              </div>

              {message.text && (
                <div role="status" className={`jt-message jt-message-${message.type}`}>
                  {message.text}
                </div>
              )}

              <div className="jt-actions">
                <button type="submit" className={`jt-submit ${isJoining ? "disabled" : ""}`} disabled={isJoining}>
                  <UserPlus size={17} />{isJoining ? "Joining teacher..." : "Join Teacher"}
                </button>
              </div>
            </form>
          </div>
          <aside className="jt-info-card"><span><KeyRound size={22} /></span><div><p className="jt-info-kicker">Need a code?</p><h2>Where do I find my teacher code?</h2><p>Your teacher will give you a code to connect your account to their classes.</p></div></aside>
          </div>

          <section className="jt-teachers-section">
            <div className="jt-section-header">
              <div className="jt-section-heading-copy"><span className="jt-section-icon"><UsersRound size={20} /></span><div>
                <h2 className="jt-section-title">My Teachers</h2>
                <p className="jt-section-subtitle">
                  {summary.teacherCount > 0
                    ? `${summary.teacherCount} connected teacher${summary.teacherCount === 1 ? "" : "s"} · ${summary.completedCount} completed assessment${summary.completedCount === 1 ? "" : "s"}`
                    : "Your connected teachers will appear here."}
                </p>
              </div></div>
              <button type="button" className="jt-secondary-button" onClick={() => navigate("/view-my-scores")}>
                <BarChart3 size={16} />View All Results
              </button>
            </div>

            {isHubLoading && <div className="jt-empty-state" role="status"><div className="jt-spinner" /><h2>Loading your teachers...</h2></div>}
            {hubError && <div className="jt-empty-state jt-empty-error" role="alert"><AlertTriangle size={30} /><h2>We could not load your teachers</h2><p>{hubError}</p></div>}
            {!isHubLoading && !hubError && teacherCards.length === 0 && (
              <div className="jt-empty-state">
                <UserPlus size={34} /><h2>You haven't joined any teachers yet.</h2><p>Enter a teacher code above to get connected.</p>
              </div>
            )}
            {!isHubLoading && !hubError && teacherCards.length > 0 && visibleTeacherCards.length === 0 && (
              <div className="jt-empty-state jt-empty-compact"><UsersRound size={28} /><h2>No teachers match your search.</h2></div>
            )}

            {!isHubLoading && !hubError && visibleTeacherCards.length > 0 && (
              <div className="jt-teacher-list">
                {visibleTeacherCards.map((teacher, index) => {
                  const isExpanded = expandedTeacherIds[teacher.id];
                  return (
                    <article className={`jt-teacher-card jt-tone-${index % 4}`} key={teacher.id}>
                      <div className="jt-teacher-main">
                        <span className="jt-teacher-icon"><UsersRound size={22} /></span>
                        <div className="jt-teacher-copy">
                          <h3 className="jt-teacher-name">{teacher.name}</h3>
                          <p className="jt-teacher-meta">Teacher code: {teacher.teacherCode}</p>
                        </div>
                        <div className="jt-teacher-summary">
                          <span><CheckCircle2 size={13} />{teacher.completedCount} completed</span>
                          {teacher.averagePercentage !== null && <span>Average: {teacher.averagePercentage}%</span>}
                        </div>
                      </div>

                      <div className="jt-teacher-classes">
                        <span className="jt-detail-label">Classes</span>
                        {teacher.classes.length > 0 ? (
                          <div className="jt-class-tags">
                            {teacher.classes.map((classItem) => (
                              <span className="jt-class-tag" key={classItem.id}>{classItem.className}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="jt-muted">No class assigned</span>
                        )}
                      </div>

                      <div className="jt-teacher-actions">
                        <button
                          type="button"
                          className="jt-activity-button"
                          onClick={() => toggleTeacherActivity(teacher.id)}
                        >
                          {isExpanded ? "Hide Activity" : "View Activity"}{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="jt-activity-panel">
                          {teacher.activity.length === 0 ? (
                            <div className="jt-activity-empty">No assessment activity yet.</div>
                          ) : (
                            teacher.activity.map((activity) => (
                              <div className="jt-activity-row" key={activity.id}>
                                <div>
                                  <button
                                    type="button"
                                    className="jt-activity-title-button"
                                    onClick={() => openAssessmentResult(activity)}
                                  >
                                    {activity.title}<ChevronRight size={14} />
                                  </button>
                                  <p className="jt-activity-date">
                                    {activity.submittedAt ? activity.submittedAt.toLocaleDateString() : "Date unavailable"}
                                  </p>
                                </div>
                                <div className="jt-activity-score">
                                  {activity.maxScore ? `${activity.score} / ${activity.maxScore}` : "Score unavailable"}
                                  {activity.percentage !== null && <span>{activity.percentage}%</span>}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
        </main>
        <footer className="jt-footer">© {new Date().getFullYear()} StudyDen. All rights reserved.</footer>
      </div>
    </div>
  );
};

export default JoinTeacher;
